import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaEdit,
  FaTrash,
  FaSearch,
  FaPlus,
  FaChevronLeft,
  FaChevronRight,
  FaCopy,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { getSchools, deleteSchool } from "../../services/schoolService";
import "./SchoolListLayer.css";

const SchoolListLayer = () => {
  const [schools, setSchools] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);

  /* ================= FETCH ================= */
  const fetchSchools = async () => {
    setLoading(true);

    try {
      const res = await getSchools();

      if (res?.status === true) {
        setSchools(res.data || []);
      } else {
        setSchools([]);
      }
    } catch (error) {
      console.error(error);
      setSchools([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this school?")) return;

    const res = await deleteSchool(id);

    if (res?.status === true) {
      toast.success("Deleted successfully");
      fetchSchools();
    } else {
      toast.error("Delete failed");
    }
  };

  /* ================= COPY ================= */
  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied!");
  };

  /* ================= SEARCH ================= */
  const filtered = schools.filter((s) =>
    `${s.name} ${s.email} ${s.plain_password} ${s.address}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(filtered.length / pageSize);
  const displayed = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="school-wrapper">
      <div className="school-card">
        {/* HEADER */}
        <div className="list-header">
          <h5>School Management</h5>
          <Link to="/add-school" className="btn-primary">
            <FaPlus /> Add School
          </Link>
        </div>

        {/* CONTROLS */}
        <div className="list-controls">
          <div className="search-box">
            <FaSearch />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>

          <div className="page-size">
            <label>Show</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span>entries</span>
          </div>
        </div>

        {/* TABLE */}
        <div className="table-responsive">
          <table className="user-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Password</th>
                <th>Reference By</th>
                <th>Address</th>
                <th>Status</th>
                <th>Logo</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {displayed.length === 0 ?
                <tr>
                  <td colSpan="8" className="no-data">
                    {loading ? "Loading..." : "No data found"}
                  </td>
                </tr>
              : displayed.map((school, i) => (
                  <tr key={school.id}>
                    <td>{(page - 1) * pageSize + i + 1}</td>

                    <td>{school.name}</td>

                    {/* EMAIL + COPY */}
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}>
                        {school.email}
                        <FaCopy
                          style={{ cursor: "pointer" }}
                          onClick={() => copyText(school.email)}
                        />
                      </div>
                    </td>

                    {/* PASSWORD + COPY */}
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          fontWeight: "bold",
                        }}>
                        {school.plain_password || "N/A"}
                        <FaCopy
                          style={{ cursor: "pointer" }}
                          onClick={() => copyText(school.plain_password)}
                        />
                      </div>
                    </td>
                    <td>
                      {school.reference_by}
                      </td>

                    <td>{school.address}</td>

                    {/* STATUS */}
                    <td>
                      <span
                        style={{
                          color: school.status === "active" ? "green" : "red",
                          fontWeight: "bold",
                        }}>
                        {school.status}
                      </span>
                    </td>

                    {/* LOGO */}
                    <td>
                      {school.logo ?
                        <img
                          src={`https://sramidcard.in/api/uploads/logos/${school.logo}`}
                          alt="logo"
                          onClick={() =>
                            window.open(
                              `https://sramidcard.in/api/uploads/logos/${school.logo}`,
                            )
                          }
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/40?text=No+Img";
                          }}
                          style={{
                            width: "40px",
                            height: "40px",
                            objectFit: "cover",
                            borderRadius: "6px",
                            border: "1px solid #ddd",
                            cursor: "pointer",
                          }}
                        />
                      : <img
                          src="https://via.placeholder.com/40?text=No+Img"
                          alt="no-img"
                        />
                      }
                    </td>

                    {/* ACTION */}
                    <td className="actions">
                      <Link to={`/edit-school/${school.id}`}>
                        <FaEdit />
                      </Link>

                      <button
                        onClick={() => handleDelete(school.id)}
                        style={{ color: "red" }}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={page === 1} onClick={() => setPage(page - 1)}>
              <FaChevronLeft />
            </button>

            <span>
              Page {page} of {totalPages}
            </span>

            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}>
              <FaChevronRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchoolListLayer;
